import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "src/components/utils/auth/assist/authContext";
import fetcher from "src/utils/fetcher";
import { InkedBalanceData } from "./useInkedBalance";

const POLL_INTERVALS = [1000, 2000, 4000, 8000, 16000]; // Exponential backoff ~31s total

interface UpdateOptions {
  onSuccess?: () => void;
  onTimeout?: () => void;
}

interface InkedBalanceResponse {
  balance: number;
  prices: {
    card: number;
  };
}

// Fetch balance directly without updating React Query cache
const fetchBalanceDirectly = async (): Promise<InkedBalanceData> => {
  const response = await fetcher({
    url: `/api/inked/balance`,
    method: "GET",
  });
  const data = response.data as InkedBalanceResponse;
  return {
    balance: data.balance,
    cardPrice: data.prices?.card ?? 1,
  };
};

export const useOptimisticBalanceUpdate = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const updateBalanceOptimistically = useCallback(
    (addedAmount: number, options?: UpdateOptions) => {
      const queryKey = ["inkedBalance", user?.email];

      // Get current cached balance
      const currentData = queryClient.getQueryData<InkedBalanceData>(queryKey);
      const currentBalance = currentData?.balance ?? 0;
      const expectedBalance = currentBalance + addedAmount;

      // Optimistically update the cache
      if (currentData) {
        queryClient.setQueryData<InkedBalanceData>(queryKey, {
          ...currentData,
          balance: expectedBalance,
        });
      }

      // Poll in background until server reflects the update
      const pollForUpdate = async () => {
        for (const delay of POLL_INTERVALS) {
          await new Promise((resolve) => setTimeout(resolve, delay));

          try {
            // Fetch directly without going through React Query cache
            const result = await fetchBalanceDirectly();

            // If server balance >= expected, update cache and we're done
            if (result.balance >= expectedBalance) {
              queryClient.setQueryData<InkedBalanceData>(queryKey, result);
              options?.onSuccess?.();
              return;
            }
            // If server balance is still stale, don't update cache - keep optimistic value
          } catch {
            // Continue polling on error
          }
        }

        // After all retries, revert to server state and notify
        queryClient.invalidateQueries({ queryKey });
        options?.onTimeout?.();
      };

      // Start polling in background (don't await)
      pollForUpdate();
    },
    [queryClient, user?.email]
  );

  return { updateBalanceOptimistically };
};
